import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import {
  Loader2, Clock, Target, CheckCircle2, XCircle, ArrowRight, ArrowLeft,
  AlertTriangle, Play, BookOpen, Trophy, BarChart3, Timer, Flag, 
  ChevronLeft, ChevronRight, Pause, Send, RefreshCw
} from "lucide-react";

const MockTest2025Page = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [paperInfo, setPaperInfo] = useState(null);
  const [testSession, setTestSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [testResults, setTestResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [myStats, setMyStats] = useState(null);
  
  const timerRef = useRef(null);
  const isMounted = useRef(true);

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      clearInterval(timerRef.current);
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearInterval(timerRef.current);
    };
  }, []);

  // Fetch paper info
  const fetchPaperInfo = useCallback(async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/pyq-2025/info`);
      if (isMounted.current) setPaperInfo(data);
    } catch (err) {
      if (isCancelError(err)) return;
      toast({ variant: "destructive", description: "Failed to load paper info" });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [toast]);

  // Fetch user stats
  const fetchMyStats = useCallback(async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/pyq-2025/my-stats`);
      if (isMounted.current) setMyStats(data);
    } catch (err) {
      if (isCancelError(err)) return;
      console.error("Failed to load stats:", err);
    }
  }, []);

  useEffect(() => {
    fetchPaperInfo();
    fetchMyStats();
  }, [fetchPaperInfo, fetchMyStats]);

  // Timer effect
  useEffect(() => {
    if (testSession && timeRemaining > 0 && !showResults) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [testSession, showResults]);

  // Start mock test
  const startMockTest = async (mode = "full", subject = null) => {
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/pyq-2025/start-mock-test`, {
        mode,
        subject,
        time_limit_minutes: mode === "full" ? 120 : 60
      });
      if (isMounted.current) {
        setTestSession(data);
        setQuestions(data.questions);
        setTimeRemaining(data.time_limit_minutes * 60);
        setAnswers({});
        setMarkedForReview(new Set());
        setCurrentIndex(0);
        setShowResults(false);
        setTestResults(null);
      }
    } catch (err) {
      if (isCancelError(err)) return;
      toast({ variant: "destructive", description: "Failed to start test" });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // Submit test
  const handleSubmitTest = async () => {
    clearInterval(timerRef.current);
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/pyq-2025/submit-mock-test`, {
        test_id: testSession.test_id,
        answers
      });
      if (isMounted.current) {
        setTestResults(data);
        setShowResults(true);
        fetchMyStats(); // Refresh stats
      }
    } catch (err) {
      if (isCancelError(err)) return;
      toast({ variant: "destructive", description: "Failed to submit test" });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // Select answer
  const selectAnswer = (questionNum, answer) => {
    setAnswers(prev => ({ ...prev, [questionNum]: answer }));
  };

  // Toggle mark for review
  const toggleMarkForReview = (questionNum) => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionNum)) {
        newSet.delete(questionNum);
      } else {
        newSet.add(questionNum);
      }
      return newSet;
    });
  };

  // Format time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get question status color
  const getQuestionStatusColor = (qNum) => {
    const answered = answers[qNum];
    const marked = markedForReview.has(qNum);
    
    if (answered && marked) return "bg-purple-500 text-white";
    if (answered) return "bg-green-500 text-white";
    if (marked) return "bg-yellow-500 text-white";
    return "bg-slate-200 text-slate-700 hover:bg-slate-300";
  };

  // Loading state
  if (loading && !paperInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Results view
  if (showResults && testResults) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Button variant="outline" onClick={() => {
          setTestSession(null);
          setShowResults(false);
          setTestResults(null);
        }} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Test Selection
        </Button>

        {/* Score Card */}
        <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <CardContent className="p-8">
            <div className="text-center">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
              <h1 className="text-3xl font-bold mb-2">Test Completed!</h1>
              <p className="text-blue-100 mb-6">UPSC Prelims 2025 GS Paper I (Set A)</p>
              
              <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-4xl font-bold">{testResults.score.raw_score}</p>
                  <p className="text-sm text-blue-100">/ {testResults.score.max_score}</p>
                  <p className="text-xs text-blue-200 mt-1">Raw Score</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-4xl font-bold text-green-300">{testResults.score.correct}</p>
                  <p className="text-xs text-blue-200 mt-1">Correct</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-4xl font-bold text-red-300">{testResults.score.wrong}</p>
                  <p className="text-xs text-blue-200 mt-1">Wrong</p>
                </div>
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-4xl font-bold text-slate-300">{testResults.score.unattempted}</p>
                  <p className="text-xs text-blue-200 mt-1">Skipped</p>
                </div>
              </div>
              
              <Badge className={`mt-6 px-4 py-2 text-lg ${
                testResults.analysis.estimated_cutoff_status === "Above Expected Cutoff" 
                  ? "bg-green-500" 
                  : testResults.analysis.estimated_cutoff_status === "Near Cutoff"
                  ? "bg-yellow-500"
                  : "bg-red-500"
              }`}>
                {testResults.analysis.estimated_cutoff_status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="mb-6 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-900 font-medium">{testResults.disclaimer}</p>
                <p className="text-sm text-amber-700 mt-1">
                  Answer Key Status: <strong>{testResults.answer_key_status.toUpperCase()}</strong> ({testResults.answer_key_source})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subject-wise Performance */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Subject-wise Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(testResults.subject_performance).map(([subject, stats]) => (
                <div key={subject}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-slate-700">{subject}</span>
                    <span className="text-sm text-slate-500">
                      {stats.correct}/{stats.total} ({stats.accuracy}%)
                    </span>
                  </div>
                  <Progress 
                    value={stats.accuracy} 
                    className={`h-3 ${stats.accuracy >= 60 ? "[&>div]:bg-green-500" : stats.accuracy >= 40 ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Analysis */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Strong Subjects
              </h3>
              {testResults.analysis.strong_subjects.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {testResults.analysis.strong_subjects.map(s => (
                    <Badge key={s} className="bg-green-600">{s}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-green-700 text-sm">Keep practicing to identify strong areas</p>
              )}
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                <XCircle className="w-5 h-5" />
                Areas to Improve
              </h3>
              {testResults.analysis.weak_subjects.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {testResults.analysis.weak_subjects.map(s => (
                    <Badge key={s} variant="outline" className="border-red-300 text-red-700">{s}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-red-700 text-sm">Great job! No major weak areas</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Question-wise Results */}
        <Card>
          <CardHeader>
            <CardTitle>Question-wise Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-10 gap-2">
              {testResults.question_results.map(qr => (
                <div
                  key={qr.question_number}
                  className={`w-full aspect-square rounded flex items-center justify-center text-sm font-bold cursor-pointer ${
                    qr.status === "correct" ? "bg-green-500 text-white" :
                    qr.status === "wrong" ? "bg-red-500 text-white" :
                    "bg-slate-200 text-slate-600"
                  }`}
                  title={`Q${qr.question_number}: ${qr.subject} - ${qr.status.toUpperCase()}`}
                >
                  {qr.question_number}
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Correct</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>Wrong</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-200 rounded"></div>
                <span>Unattempted</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Retry Button */}
        <div className="flex justify-center mt-8">
          <Button 
            size="lg"
            onClick={() => startMockTest("full")}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Attempt Again
          </Button>
        </div>
      </div>
    );
  }

  // Test in progress view
  if (testSession && questions.length > 0) {
    const currentQuestion = questions[currentIndex];
    
    return (
      <div className="min-h-screen bg-slate-100">
        {/* Header with Timer */}
        <div className="sticky top-0 z-50 bg-white shadow-md">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h1 className="font-bold text-slate-900">UPSC 2025 GS-I Mock Test</h1>
                <Badge variant="outline">
                  Q {currentIndex + 1} / {questions.length}
                </Badge>
              </div>
              
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  timeRemaining < 600 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                }`}>
                  <Timer className="w-5 h-5" />
                  <span className="font-mono font-bold text-lg">{formatTime(timeRemaining)}</span>
                </div>
                
                <Button 
                  variant="destructive" 
                  onClick={handleSubmitTest}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Test
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
          {/* Question Panel */}
          <div className="flex-1">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="bg-blue-100 text-blue-700">Q{currentQuestion.question_number}</Badge>
                  <Badge variant="outline">{currentQuestion.subject}</Badge>
                  {currentQuestion.subtopic && (
                    <Badge variant="outline" className="text-xs">{currentQuestion.subtopic}</Badge>
                  )}
                </div>

                <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-line">
                  {currentQuestion.question}
                </p>

                {/* Options */}
                <div className="space-y-3">
                  {Object.entries(currentQuestion.options || {}).map(([key, value]) => {
                    const isSelected = answers[currentQuestion.question_number] === key;
                    return (
                      <button
                        key={key}
                        onClick={() => selectAnswer(currentQuestion.question_number, key)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          isSelected 
                            ? "border-blue-500 bg-blue-50" 
                            : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                            isSelected ? "bg-blue-500 text-white" : "bg-slate-200 text-slate-700"
                          }`}>
                            {key}
                          </span>
                          <span className="text-slate-700">{value}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => selectAnswer(currentQuestion.question_number, null)}
                      disabled={!answers[currentQuestion.question_number]}
                    >
                      Clear Response
                    </Button>
                    <Button
                      variant={markedForReview.has(currentQuestion.question_number) ? "default" : "outline"}
                      onClick={() => toggleMarkForReview(currentQuestion.question_number)}
                      className={markedForReview.has(currentQuestion.question_number) ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      {markedForReview.has(currentQuestion.question_number) ? "Marked" : "Mark for Review"}
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                      disabled={currentIndex === 0}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                      disabled={currentIndex === questions.length - 1}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Question Navigator */}
          <div className="w-80">
            <Card className="sticky top-24">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Question Navigator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {questions.map((q, idx) => (
                    <button
                      key={q.question_number}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-full aspect-square rounded text-sm font-bold transition-all ${
                        idx === currentIndex ? "ring-2 ring-blue-500 ring-offset-2" : ""
                      } ${getQuestionStatusColor(q.question_number)}`}
                    >
                      {q.question_number}
                    </button>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="space-y-2 text-xs border-t pt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span>Answered ({Object.keys(answers).filter(k => answers[k]).length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                    <span>Marked for Review ({markedForReview.size})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-purple-500 rounded"></div>
                    <span>Answered & Marked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <span>Not Visited</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Main selection view
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <Badge className="bg-red-500 text-white mb-4">NEW - 2025</Badge>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">UPSC Prelims 2025</h1>
        <p className="text-slate-600 max-w-2xl mx-auto">
          Practice the actual UPSC CSE Prelims 2025 GS Paper I questions.<br/>
          <strong className="text-slate-800">100 questions • 2 hours • Real exam experience</strong>
        </p>
      </div>

      {/* Paper Info */}
      {paperInfo && (
        <Card className="mb-8 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <BookOpen className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                <p className="text-2xl font-bold">{paperInfo.total_questions}</p>
                <p className="text-sm text-slate-300">Questions</p>
              </div>
              <div className="text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-green-400" />
                <p className="text-2xl font-bold">{paperInfo.duration_minutes} min</p>
                <p className="text-sm text-slate-300">Duration</p>
              </div>
              <div className="text-center">
                <Target className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                <p className="text-2xl font-bold">{paperInfo.max_marks}</p>
                <p className="text-sm text-slate-300">Max Marks</p>
              </div>
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                <p className="text-lg font-bold">+2 / -0.67</p>
                <p className="text-sm text-slate-300">Marking Scheme</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Answer Key Status */}
      <Card className="mb-8 bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-900 font-medium">
                ⚠️ Answer Key Status: <strong>{paperInfo?.answer_key_status?.toUpperCase()}</strong>
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Source: {paperInfo?.answer_key_source} • Last Updated: {paperInfo?.answer_key_last_updated}
              </p>
              <p className="text-sm text-amber-600 mt-1">
                Your scores will be automatically recalculated when the official UPSC key is released.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Stats */}
      {myStats && myStats.total_attempts > 0 && (
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Your Progress on UPSC 2025 Paper
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">{myStats.total_questions_attempted}</p>
                <p className="text-sm text-slate-600">Questions Practiced</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{myStats.mock_tests_completed}</p>
                <p className="text-sm text-slate-600">Mock Tests</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{myStats.best_mock_test_score}</p>
                <p className="text-sm text-slate-600">Best Score</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-600">{myStats.weak_subjects?.length || 0}</p>
                <p className="text-sm text-slate-600">Weak Areas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Options */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Full Mock Test */}
        <Card className="border-2 border-blue-200 hover:border-blue-400 transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Play className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Full Mock Test</h3>
                <p className="text-sm text-slate-500">100 questions • 2 hours</p>
              </div>
            </div>
            <p className="text-slate-600 mb-4">
              Experience the real UPSC Prelims exam format with all 100 questions and strict time limits.
            </p>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              onClick={() => startMockTest("full")}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Start Full Test
            </Button>
          </CardContent>
        </Card>

        {/* Subject-wise Practice */}
        <Card className="border-2 border-purple-200 hover:border-purple-400 transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Subject-wise Practice</h3>
                <p className="text-sm text-slate-500">Focus on specific subjects</p>
              </div>
            </div>
            <p className="text-slate-600 mb-4">
              Practice questions from specific subjects to strengthen your weak areas.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {paperInfo?.subjects?.slice(0, 6).map(subject => (
                <Button 
                  key={subject}
                  variant="outline"
                  size="sm"
                  onClick={() => startMockTest("subject_wise", subject)}
                  disabled={loading}
                >
                  {subject}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subject Distribution */}
      {paperInfo?.subject_distribution && (
        <Card>
          <CardHeader>
            <CardTitle>Subject Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(paperInfo.subject_distribution).map(([subject, count]) => (
                <div key={subject} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{subject}</span>
                  <Badge variant="outline">{count} Qs</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MockTest2025Page;
